import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { agentDisplayLabel } from '../../utils/agentDisplay';
import { searchPeople } from '../../utils/peopleSearch';
import usePopTransition from './usePopTransition';
import './PeoplePicker.css';

const MAX_ROWS = 50;

/**
 * Pick people from a list with a search box: a button that opens a panel with a box to type in, and the people who match, each with their
 * position and team under the name. Type "ani", "royal flush" or "team lead"; the arrow keys move, Enter adds, Escape closes.
 *
 * It stays open after a person is added, so several can be added one after the other; the person added leaves the list (the caller passes
 * the list without them). The list is drawn on <body>, so a table cell's overflow cannot clip it, and it opens with the suite's pop motion.
 *
 * `people` are { id, username, real_name, team, role, role_label }. `onPick(person)` is called for each choice.
 *
 * It also picks other things from a searchable list (Callie's positions and teams): pass `searchFn(items, query)`, `labelOf(item)`, `subOf(item)` and
 * `placeholder`, and `field` to draw it as a combobox instead of a small "+" button: the field itself is the search box (its label is the placeholder, a chevron
 * on the right), and the matches drop down under it as wide as the field, with no second box to type in.
 */
const defaultSub = (p) => [p.role_label || p.role, p.team].filter(Boolean).join(' · ');

export default function PeoplePicker({
  people,
  onPick,
  label = 'Add a person',
  searchLabel = 'Find a person',
  emptyText = 'There is nobody left to add.',
  disabled = false,
  className = '',
  searchFn = searchPeople,
  labelOf = agentDisplayLabel,
  subOf = defaultSub,
  placeholder = 'Name, team or position',
  hint = 'Enter adds the highlighted one. Escape closes.',
  field = false
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState(null);
  const [added, setAdded] = useState('');
  const pop = usePopTransition(open);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useRef(`people-picker-${Math.random().toString(36).slice(2, 8)}`).current;

  const found = useMemo(() => searchFn(people, query), [searchFn, people, query]);
  const shown = found.slice(0, MAX_ROWS);
  const current = Math.min(active, Math.max(shown.length - 1, 0));

  const close = useCallback((focusButton = true) => {
    setOpen(false);
    if (focusButton && !field && buttonRef.current) buttonRef.current.focus(); // a field keeps the focus it has: it is the box being typed in
  }, [field]);

  const openPanel = () => {
    if (disabled) return;
    setQuery('');
    setActive(0);
    setAdded('');
    setOpen(true);
  };

  // Placed under the button, or above it when there is no room, in viewport coordinates (it is portalled to <body>).
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < 300 && r.top > below;
    const width = field ? r.width : Math.min(340, window.innerWidth - 16);
    setPlace({
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      width,
      maxHeight: Math.max(220, Math.min(420, (up ? r.top : below) - 12)),
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      up
    });
  }, [open, field]);

  useEffect(() => {
    if (open && place && inputRef.current) inputRef.current.focus();
  }, [open, place]);

  // A click outside, a scroll of the page or a resize closes it, as a menu does.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (buttonRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      close(false);
    };
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const el = panelRef.current.querySelector(`[data-index="${current}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, current]);

  const choose = (person) => {
    if (!person) return;
    onPick(person);
    setAdded(`Added ${labelOf(person)}`);
    // The list loses this person, so the highlight stays where it is and lands on the next one.
    if (field) { setQuery(''); setActive(0); }
    if (inputRef.current) inputRef.current.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') { close(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(current + 1, shown.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(current - 1, 0)); return; }
    if (e.key === 'Home' && !query) { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End' && !query) { e.preventDefault(); setActive(Math.max(shown.length - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); choose(shown[current]); }
  };

  const optionId = (i) => `${listId}-o${i}`;

  return (
    <>
      {field ? (
        // The combobox: typing in the field filters the list under it; a click anywhere on the field (the chevron too) opens or closes it.
        <div
          ref={buttonRef}
          className={`people-picker__field${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''} ${className}`.trim()}
          onClick={(e) => {
            if (disabled) return;
            if (e.target === inputRef.current) { if (!open) openPanel(); return; }
            if (open) close(false); else { openPanel(); if (inputRef.current) inputRef.current.focus(); }
          }}
        >
          <input
            ref={inputRef}
            type="text"
            className="people-picker__field-input"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={open && shown.length ? optionId(current) : undefined}
            aria-label={searchLabel}
            placeholder={label}
            value={open ? query : ''}
            disabled={disabled}
            onFocus={() => { if (!open && !disabled) openPanel(); }}
            onChange={(e) => { if (!open) setOpen(true); setQuery(e.target.value); setActive(0); setAdded(''); }}
            onKeyDown={(e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { e.preventDefault(); openPanel(); return; } onKeyDown(e); }}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="people-picker__chevron" aria-hidden="true">▾</span>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={`btn btn-secondary btn-sm people-picker__button ${className}`.trim()}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => (open ? close() : openPanel())}
        >
          <span aria-hidden="true">+</span> {label}
        </button>
      )}
      {pop.mounted && place && createPortal(
        <div
          ref={panelRef}
          className={`people-picker ui-pop${place.up ? ' ui-pop--up' : ''}${pop.closing ? ' is-closing' : ''}`}
          style={{ left: place.left, width: place.width, maxHeight: place.maxHeight, ...(place.up ? { bottom: place.bottom } : { top: place.top }) }}
        >
          {!field && (
          <input
            ref={inputRef}
            type="search"
            className="people-picker__search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={shown.length ? optionId(current) : undefined}
            aria-label={searchLabel}
            placeholder={placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); setAdded(''); }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          )}
          <div id={listId} role="listbox" aria-label={label} className="people-picker__list">
            {shown.map((p, i) => (
              <div
                key={p.id}
                id={optionId(i)}
                data-index={i}
                role="option"
                aria-selected={i === current}
                className={`people-picker__option${i === current ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(p)}
              >
                <span className="people-picker__name">{labelOf(p)}</span>
                <span className="people-picker__sub">{subOf(p)}</span>
              </div>
            ))}
            {!people.length ? <p className="people-picker__note">{emptyText}</p> : null}
            {people.length > 0 && !found.length ? <p className="people-picker__note">No one matches &ldquo;{query.trim()}&rdquo;.</p> : null}
            {found.length > MAX_ROWS ? <p className="people-picker__note">{found.length - MAX_ROWS} more, type to narrow the list.</p> : null}
          </div>
          <p className="people-picker__foot" role="status">
            {added || hint}
          </p>
        </div>,
        document.body
      )}
    </>
  );
}
